/**
 * Service notification system for the document store.
 *
 * This module provides direct service method calls to notify effect services
 * about processed actions, replacing the previous lastProcessedAction subscription pattern.
 * Services receive actions through their onAction() methods instead of subscribing
 * to store state changes.
 */
import type {DocumentActionWithOptionalPayload} from "./types";
import type {DocumentSaveService} from "../effects/DocumentSaveService";
import type {ActionRecordService} from "../effects/ActionRecordService";
import type {PatchHandlerService} from "../effects/PatchHandlerService";
import type {DocumentConfigService} from "../effects/DocumentConfigService";
import type {DocumentMetaSyncService} from "../effects/DocumentMetaSyncService";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Collection of effect services that receive action notifications.
 * All fields are optional because services may be created after the store
 * (the services object is populated after store creation to break circular dependencies).
 */
export interface DocumentServices {
    documentSaveService?: DocumentSaveService;
    actionRecordService?: ActionRecordService;
    patchHandlerService?: PatchHandlerService;
    documentConfigService?: DocumentConfigService;
    documentMetaSyncService?: DocumentMetaSyncService;
}

/**
 * Listener callback type for action notifications.
 * Used by React hooks (e.g., useOnDocumentAction) to subscribe to action events.
 */
export type ActionListener = (action: DocumentActionWithOptionalPayload) => void;

/**
 * Event bus for broadcasting action notifications to arbitrary listeners.
 * This enables React hooks and other non-service consumers to receive action events
 * without coupling to the store's internal state.
 */
export class ActionEventBus {
    private listeners = new Set<ActionListener>();

    subscribe(listener: ActionListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    emit(action: DocumentActionWithOptionalPayload): void {
        for (const listener of this.listeners) {
            try {
                listener(action);
            } catch (e) {
                FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
            }
        }
    }
}

/**
 * Notifies all registered effect services and event bus listeners about a processed action.
 *
 * @param action - The action that was processed by the store
 * @param services - The collection of effect services to notify
 * @param actionEventBus - Optional event bus for notifying non-service listeners
 */
export function notifyServicesOfAction(
    action: DocumentActionWithOptionalPayload,
    services: DocumentServices,
    actionEventBus?: ActionEventBus,
): void {
    try {
        services.documentSaveService?.onAction(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
    try {
        services.actionRecordService?.onAction(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
    try {
        services.patchHandlerService?.onAction(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
    try {
        services.documentConfigService?.onAction(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
    try {
        services.documentMetaSyncService?.onAction(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
    try {
        actionEventBus?.emit(action);
    } catch (e) {
        FluxLogger.captureError(e instanceof Error ? e : new Error(String(e)));
    }
}
